import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExploreTab as ExploreTabType } from "domains/inference/interfaces/Explore";
import { getModelThumbnail } from "domains/models/utils";
import { CarouselNavButton } from "domains/ui/components/Carousel";
import Icon from "domains/ui/components/Icon";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

import {
  Box,
  Center,
  Flex,
  HStack,
  Image,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";

interface ExploreTabsProps {
  tabs: ExploreTabType[] | undefined;
  isSuggestsAvailable?: boolean;
  isSuggestsNotified?: boolean;
  suggestModel?: GetModelsByModelIdApiResponse["model"];
  value: string;
  status: { [key: string]: boolean };
  onChange: (value: string) => void;
}

export default function ExploreTabs({
  tabs,
  isSuggestsAvailable = false,
  isSuggestsNotified = false,
  suggestModel,
  value,
  status,
  onChange,
}: ExploreTabsProps) {
  const valueRef = useRef(value);
  const initialTabIndex = useMemo(
    () => tabs?.findIndex((tab) => tab.id === valueRef.current),
    [tabs]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      skipSnaps: true,
      startIndex: Math.max(0, (initialTabIndex ?? 0) - 1),
    },
    [WheelGesturesPlugin({ forceWheelAxis: "x" })]
  );

  const checkCarouselState = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  const scrollCarousel = useCallback(
    (direction: "next" | "prev") => {
      if (!emblaApi) return;
      const slideCount = Math.max(1, emblaApi.slidesInView().length - 3);
      const currentIndex = emblaApi.selectedScrollSnap();
      const nextIndex =
        direction === "next"
          ? currentIndex + slideCount
          : currentIndex - slideCount;
      emblaApi.scrollTo(nextIndex);
      checkCarouselState();
    },
    [emblaApi, checkCarouselState]
  );

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("init", checkCarouselState);
    emblaApi.on("resize", checkCarouselState);
    emblaApi.on("slidesInView", checkCarouselState);
    return () => {
      emblaApi.off("init", checkCarouselState);
      emblaApi.off("resize", checkCarouselState);
      emblaApi.off("slidesInView", checkCarouselState);
    };
  }, [emblaApi, checkCarouselState]);

  return (
    <HStack
      pt={4}
      borderBottomWidth={1}
      borderBottomColor="border.500"
      bgColor="background.500"
      spacing={4}
    >
      {isSuggestsAvailable && (
        <ExploreTab
          value="suggests"
          icon={<Icon id="Ui/StarsAlt" h="16px" />}
          thumbnailUrl={
            suggestModel?.thumbnail?.assetId
              ? `${process.env.NEXT_PUBLIC_CDN_URL}/thumbnails/${suggestModel.thumbnail.assetId}`
              : undefined
          }
          imgUrl={
            getModelThumbnail(suggestModel, {
              width: 256,
            }).url
          }
          title="Model"
          isActive={value === "suggests"}
          hasNotification={isSuggestsNotified}
          onSelect={onChange}
        />
      )}

      <Box
        ref={emblaRef}
        pos="relative"
        overflow="hidden"
        onLoad={checkCarouselState}
      >
        <Flex
          sx={{
            backfaceVisibility: "hidden",
            touchAction: "pan-y",
          }}
          align="stretch"
          ml={-4}
        >
          {tabs
            ? tabs.map((tab) => (
                <Box key={tab.id} flex="0" w="100%" pl={4}>
                  <ExploreTab
                    value={tab.id}
                    thumbnailUrl={tab.thumbnailUrl}
                    imgUrl={tab.imgUrl}
                    title={tab.title}
                    isActive={value === tab.id}
                    hasNotification={status[tab.id] === false}
                    onSelect={onChange}
                  />
                </Box>
              ))
            : _.range(3).map((idx) => (
                <Box key={`skeleton-${idx}`} flex="0" w="100%" pl={4}>
                  <ExploreTab isSkeleton />
                </Box>
              ))}
        </Flex>

        {canScrollPrev && (
          <>
            <Box
              pos="absolute"
              zIndex="docked"
              top={0}
              bottom={0}
              left={0}
              w="180px"
              bg="linear-gradient(90deg, #121212 0%, rgba(18, 18, 18, 0) 100%)"
              pointerEvents="none"
            />
            <CarouselNavButton
              variant="secondary"
              direction="prev"
              onClick={() => scrollCarousel("prev")}
              pos="absolute"
              zIndex="docked"
              left={4}
              top="30px"
            />
          </>
        )}

        {canScrollNext && (
          <>
            <Box
              pos="absolute"
              zIndex="docked"
              top={0}
              right={0}
              bottom={0}
              w="180px"
              bg="linear-gradient(270deg, #121212 0%, rgba(18, 18, 18, 0) 100%)"
              pointerEvents="none"
            />
            <CarouselNavButton
              variant="secondary"
              direction="next"
              onClick={() => scrollCarousel("next")}
              pos="absolute"
              zIndex="docked"
              right={4}
              top="30px"
            />
          </>
        )}
      </Box>
    </HStack>
  );
}

// ------------------------------------

type ExploreTabProps =
  | {
      value: string;
      icon?: React.ReactNode;
      thumbnailUrl?: string;
      imgUrl?: string;
      title: React.ReactNode;
      isActive?: boolean;
      hasNotification?: boolean;
      onSelect: (id: string) => void;
      isSkeleton?: false;
    }
  | {
      value?: undefined;
      icon?: undefined;
      thumbnailUrl?: undefined;
      imgUrl?: undefined;
      title?: undefined;
      isActive?: undefined;
      hasNotification?: undefined;
      onSelect?: undefined;
      isSkeleton: true;
    };

function ExploreTab({
  value,
  icon,
  thumbnailUrl,
  imgUrl,
  title,
  isActive = false,
  hasNotification = false,
  isSkeleton = false,
  onSelect,
}: ExploreTabProps) {
  const handleClick = useCallback(() => {
    if (onSelect) onSelect(value);
  }, [onSelect, value]);

  return (
    <Box flex={0}>
      <VStack
        pos="relative"
        w="auto"
        minW="92px"
        maxW="127px"
        pb={4}
        cursor="pointer"
        data-group
        onClick={handleClick}
        spacing={3}
      >
        {isSkeleton ? (
          <Skeleton w="100%" h="66px" />
        ) : (
          <Center
            overflow="hidden"
            w="100%"
            h="66px"
            color={isActive ? "textPrimary" : "textTertiary"}
            borderTopRadius="md"
            _groupHover={{ color: "textPrimary" }}
            transition="color 200ms linear"
            bgColor="backgroundTertiary.500"
          >
            {imgUrl || thumbnailUrl ? (
              <Image
                w="full"
                h="full"
                opacity={isActive ? 1 : 0.7}
                _groupHover={{ opacity: 1 }}
                transition="opacity 200ms linear"
                alt="category illustration"
                fallbackSrc={thumbnailUrl}
                fit="cover"
                src={imgUrl}
              />
            ) : (
              icon
            )}
          </Center>
        )}

        <Box pos="relative" overflow="hidden" w="100%">
          <ExploreTabText
            isSkeleton={isSkeleton}
            hasNotification={isSkeleton ? false : hasNotification}
            isActive={isActive}
            title={title}
          />

          <ExploreTabText
            isAbove
            hasNotification={hasNotification}
            isActive={isActive}
            title={title}
          />
        </Box>

        <Box
          pos="absolute"
          right={0}
          bottom={0}
          left={0}
          h="1.5px"
          opacity={isActive ? 1 : 0}
          _groupHover={!isActive ? { opacity: 0.4 } : undefined}
          transition="opacity 200ms cubic-bezier(0.2,0,0,1)"
          bgColor="textPrimary"
        />
      </VStack>
    </Box>
  );
}

// ------------------------------------

interface ExploreTabTextProps {
  isAbove?: boolean;
  hasNotification: boolean;
  isSkeleton?: boolean;
  isActive: boolean;
  title: React.ReactNode;
}

function ExploreTabText({
  isAbove = false,
  hasNotification = false,
  isSkeleton = false,
  isActive = false,
  title,
}: ExploreTabTextProps) {
  return (
    <HStack
      pos={isAbove ? "absolute" : undefined}
      top={isAbove ? 0 : undefined}
      justify="center"
      overflow="hidden"
      w="full"
      visibility={hasNotification !== isAbove ? "visible" : "hidden"}
    >
      {isSkeleton ? (
        <SkeletonText w="50px" noOfLines={1} skeletonHeight="20px" />
      ) : (
        <Text
          opacity={isActive ? 1 : 0.5}
          _groupHover={{ color: "textPrimary" }}
          transition="opacity 200ms linear"
          isTruncated
          size="body.md"
        >
          {title}
        </Text>
      )}

      {!isAbove && (
        <Box flex={0} visibility={hasNotification ? "visible" : "hidden"}>
          <Box w="8px" h="8px" borderRadius="50%" bgColor="primary.500" />
        </Box>
      )}
    </HStack>
  );
}
